import * as React from 'react';
import styles from './new-post.module.scss';
import { WModal } from '../../wrappers/wrapper';
import NewPostModal from '../new-post-modal/new-post-modal';
import UploadImage from '../../organisms/add-image-modal/add-image-modal';
import { useContext, useEffect, useReducer, useState } from 'react';
import YoutubeDialog from '../../organisms/youtube-dialog/youtube-dialog';
import dbService from '../../../services/database.service';
import { TArticle, TChannel, TTopic } from '../../../models/common';
import spicaStorageService from '../../../services/spica.storage.service';
import { UserContext } from '../../../contexts/userContext';
import { ArticleContext } from '../../../contexts/articleContext';
import Button from 'components/atoms/button/button';
import useDeviceType from 'custom-hooks/useDeviceType';
import { EmojiClickData } from 'emoji-picker-react/dist/types/exposedTypes';
import { ProjectContext } from 'contexts/projectContext';

const NewPost: React.FC = () => {
  const { isDesktop } = useDeviceType();

  const { user, currentPage } = useContext(UserContext);
  const { articles, setArticles } = useContext(ArticleContext);
  const { channels } = useContext(ProjectContext);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [openPostModal, setOpenPostModal] = useState(false);

  const defaultArticle = {
    cover_image: '',
    title: '',
    content: '',
    publisher: user._id,
    channel: '',
    youtube_id: '',
    project: process.env.REACT_APP_PROJECT_ID,
    topics: [],
  };
  const [topics, setTopics] = useState<TTopic[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<TChannel>(channels[0]);
  const [isYoutubeDialogVisible, setIsYoutubeDialogVisible] = useState(false);
  const [isUploadImageDialogVisible, setIsUploadImageDialogVisible] = useState(false);
  const [isUploadCoverImageDialogVisible, setIsUploadCoverImageDialogVisible] = useState(false);
  const [article, setArticle] = useState<TArticle>(defaultArticle);
  const [activeTextFieldIndex, setActiveTextFieldIndex] = useState(-1);
  const [alignments, setAlignments] = useState<string[]>([]);
  const [textFields, setTextFields] = useState(['']);


  useEffect(() => {
    if (!selectedChannel) return;

    setArticle({ ...article, channel: selectedChannel._id });
    dbService
      .getTopics({ filter: { channels: { $in: [selectedChannel?._id] } } })
      .then((res) => setTopics(res));
  }, [selectedChannel]);

  useEffect(() => {
    forceUpdate();
  }, [alignments]);

  const handleOpenPostModal = () => {
    const defaultValueOfSelectedChannel = channels.find((page) => page.title === currentPage.title);
    if (defaultValueOfSelectedChannel) {
      setSelectedChannel(defaultValueOfSelectedChannel);
    }

    setOpenPostModal(true);
  };

  const handleClosePostModal = () => {
    setOpenPostModal(false);
    setArticle(defaultArticle);
    setTextFields(['']);
  };

  const handleImageUpload = async (image: any, type: 'post' | 'cover') => {
    const res = await spicaStorageService.upload(image);
    switch (type) {
      case 'post':
        textFields.push('');
        setTextFields(textFields);
        const imgIndex = activeTextFieldIndex === -1 ? 0 : activeTextFieldIndex;
        images[imgIndex] = res.url;
        alignments[imgIndex] = 'center';
        setAlignments(alignments);
        setImages(images);
        break;
      case 'cover':
        setArticle({ ...article, cover_image: res.url });
        break;
    }

    forceUpdate();
  };

  const handlePublish = async () => {
    const content = prepareContent();
    dbService
      .insertArticle({ ...article, channel: selectedChannel?._id, publisher: user._id, content })
      .then((newArticle) => {
        newArticle.publisher = user as any;
        setArticles([...articles, newArticle]);
      });

    setTextFields(['']);
    setImages([]);

    handleClosePostModal();
  };

  const prepareContent = () => {
    let content: string = '';
    textFields.map((textField, index) => {
      content += content.length ? `<br> ${textField}` : textField;
      const isImageExist = images[index];
      if (isImageExist) {
        const imageStyle = `style="width: ${alignments[index] === 'center' ? '100%' : '50%'};"`;
        content += `<img src="${images[index]}" ${imageStyle} />`;
      }
    });
    return content;
  };

  const handleClickEmoji = (emojiData: EmojiClickData) => {
    if (activeTextFieldIndex === -1) {
      setArticle((prev) => ({ ...prev, title: (prev.title || '') + emojiData.emoji }));
      return;
    }
    textFields[activeTextFieldIndex] = textFields[activeTextFieldIndex] + emojiData.emoji;
    setTextFields([...textFields]);
  };

  const handleChangeAlignments = (_alignments: string[]) => {
    setAlignments(_alignments);
    forceUpdate();
  };

  return (
    <div className={styles.newPost}>
      <Button
        type={isDesktop ? 'primary' : 'base'}
        radius="2rem"
        onClick={handleOpenPostModal}
        className={styles.newPostButton}
      >
        New post
      </Button>

      <WModal
        open={openPostModal}
        onClose={handleClosePostModal}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <NewPostModal
          textFields={textFields}
          alignments={alignments}
          images={images}
          topics={topics}
          article={article}
          channels={channels}
          selectedChannel={selectedChannel!}
          onClose={handleClosePostModal}
          onClickYoutube={() => setIsYoutubeDialogVisible(true)}
          onClickUploadImage={() => setIsUploadImageDialogVisible(true)}
          onClickUploadCoverImage={() => setIsUploadCoverImageDialogVisible(true)}
          onPublish={() => handlePublish()}
          onChangeData={(data) => setArticle({ ...article, ...data })}
          onChangeChannel={setSelectedChannel}
          onChangeActiveTextFiled={setActiveTextFieldIndex}
          onChangeAlignments={handleChangeAlignments}
          onChangeTextFileds={setTextFields}
          onChooseEmoji={handleClickEmoji}
        />
      </WModal>
      {isUploadCoverImageDialogVisible && (
        <UploadImage
          links={false}
          onClose={() => setIsUploadCoverImageDialogVisible(false)}
          onImageUpload={(image) => handleImageUpload(image, 'cover')}
        />
      )}
      {isUploadImageDialogVisible && (
        <UploadImage
          links={false}
          onClose={() => setIsUploadImageDialogVisible(false)}
          onImageUpload={(image) => handleImageUpload(image, 'post')}
        />
      )}
      <YoutubeDialog
        open={isYoutubeDialogVisible}
        onClose={(id) => {
          setIsYoutubeDialogVisible(false);
          setArticle({ ...article, youtube_id: id });
        }}
      />
    </div>
  );
};

export default NewPost;
